<script setup lang="ts">
import { computed, toRefs } from 'vue';

import { useYardSpotPresenter } from '@satellite/composables/useYardSpotPresenter';
import YardAreaPresenter from './YardAreaPresenter.vue';

import type { Maybe } from '@nova/core';
import type { YardSpotDetails } from '@satellite/composables/useYardSpotPresenter';
import type { YardAreaDetails } from '@satellite/composables/useYardAreaPresenter';

const props = withDefaults(
  defineProps<{
    size?: 'regular' | 'small';
    spot?: Maybe<YardSpotDetails>;
    spotId?: Maybe<string>;
    area?: Maybe<YardAreaDetails>;
  }>(),
  {
    size: 'regular',
    spot: null,
    spotId: null,
    area: null
  }
);

const { spotId, spot } = toRefs(props);

const {
  data: spotPresenter,
  isLoading,
  onIntersect
} = useYardSpotPresenter({
  spotId,
  placeholderSpotData: spot,
  mode: 'live'
});

const areaId = computed(() => spotPresenter.value?.spotAreaId ?? null);
const spotCode = computed(() => spotPresenter.value?.code ?? null);
</script>

<template>
  <div
    v-intersect="{
      handler: onIntersect,
      options: {
        threshold: [0.5]
      }
    }">
    <v-skeleton-loader :loading="isLoading" type="text">
      <div class="d-flex flex-row gap-1 align-center yard-spot-presenter" style="min-width: 50px">
        <yard-area-presenter :area="area" :area-id="areaId" :size="size" style="min-width: 50px" />
        -
        <span
          :class="size === 'small' ? 'typography-body-sm-bold' : 'typography-body-md-bold'"
          data-testid="yard-spot-presenter-spot-code">
          {{ spotCode }}
        </span>
      </div>
    </v-skeleton-loader>
  </div>
</template>

<style scoped>
:deep(.v-skeleton-loader__text) {
  margin: 6px 0 !important;
}
</style>
